import { DataSource } from '@futrli-sdk/organisations';
import { DatasourceListingItem } from '@futrli-sdk/workspaces';

import { DATASOURCES_STATUS } from '../../app-core/constants';

export const getStatusValue = (datasource: DataSource | DatasourceListingItem) => {
  const dsli = datasource as DatasourceListingItem;
  const ds = datasource as DataSource;
  return (dsli.sync_status || ds.status.value).toLowerCase() as DATASOURCES_STATUS;
};

export const isInUseForConnectJob = (datasource: DataSource) =>
  [
    DATASOURCES_STATUS.sync_success,
    DATASOURCES_STATUS.sync_failure,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_update_status,
    DATASOURCES_STATUS.disconnect_failure,
    DATASOURCES_STATUS.disconnect_start
  ].includes(getStatusValue(datasource));

export const requiresAction = (datasource: DataSource) =>
  [
    DATASOURCES_STATUS.never_connected,
    DATASOURCES_STATUS.connect_failure,
    DATASOURCES_STATUS.connect_timeout,
    DATASOURCES_STATUS.disconnect_success
  ].includes(datasource.status.value as DATASOURCES_STATUS);

export const isReconnectNeeded = (datasource: DataSource) =>
  [
    DATASOURCES_STATUS.never_connected,
    DATASOURCES_STATUS.connect_failure,
    DATASOURCES_STATUS.connect_timeout,
    DATASOURCES_STATUS.disconnect_success
  ].includes(datasource.status.value as DATASOURCES_STATUS);

export const isInUseForSyncJob = (datasource: DataSource) =>
  [
    DATASOURCES_STATUS.connect_failure,
    DATASOURCES_STATUS.sync_success,
    DATASOURCES_STATUS.sync_failure,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_update_status,
    DATASOURCES_STATUS.disconnect_failure,
    DATASOURCES_STATUS.disconnect_start
  ].includes(getStatusValue(datasource));

export const isConnected = (datasource: DataSource) =>
  [
    DATASOURCES_STATUS.connect_success,
    DATASOURCES_STATUS.delete_failure,
    DATASOURCES_STATUS.delete_start,
    DATASOURCES_STATUS.disconnect_failure,
    DATASOURCES_STATUS.disconnect_start,
    DATASOURCES_STATUS.sync_failure,
    DATASOURCES_STATUS.sync_initial_start,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_success,
    DATASOURCES_STATUS.sync_initial_success,
    DATASOURCES_STATUS.sync_initial_update_status,
    DATASOURCES_STATUS.sync_initial_failure,
    DATASOURCES_STATUS.sync_update_status
  ].includes(getStatusValue(datasource));

export const isInConnectStart = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.connect_start;

export const isInConnectSuccess = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.connect_success;

export const isInConnectFailure = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.connect_failure;

export const isInConnectTimeout = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.connect_timeout;

export const isInSyncInitialStart = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_initial_start;

export const isInSyncInitialSuccess = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_initial_success;

export const isInSyncStart = (datasource: DataSource) => getStatusValue(datasource) === DATASOURCES_STATUS.sync_start;

export const isInSyncFailure = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_failure;

export const isInSyncInitialFailure = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_initial_failure;

export const isInSyncSuccess = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_success;

export const isInSyncInitialUpdateStatus = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_initial_update_status;

export const isInSyncUpdateStatus = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.sync_update_status;

export const isInSyncing = (datasource: DataSource | DatasourceListingItem) =>
  [
    DATASOURCES_STATUS.sync_initial_start,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_initial_update_status,
    DATASOURCES_STATUS.sync_update_status
  ].includes(getStatusValue(datasource));

export const isInSyncingByStatus = (datasourceStatus: DATASOURCES_STATUS) =>
  [
    DATASOURCES_STATUS.sync_initial_start,
    DATASOURCES_STATUS.sync_start,
    DATASOURCES_STATUS.sync_initial_update_status,
    DATASOURCES_STATUS.sync_update_status
  ].includes(datasourceStatus);

export const isInPendingSetup = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.pending_setup;

export const isInInitialSyncing = (datasource: DataSource) =>
  [DATASOURCES_STATUS.sync_initial_start, DATASOURCES_STATUS.sync_initial_update_status].includes(
    getStatusValue(datasource)
  );

export const isInNeverConnected = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.never_connected;

export const isInDisconnectStart = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.disconnect_start;

export const isInDisconnectFailure = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.disconnect_failure;

export const isInDisconnectSuccess = (datasource: DataSource) =>
  getStatusValue(datasource) === DATASOURCES_STATUS.disconnect_success;
